// HTTP client for the Rails server, replacing src-tauri/src/api.rs. Same
// endpoints, same JSON contract, same Bearer-token auth — the server is
// unchanged. Responses use the server's snake_case; the bridge layer maps
// them to the camelCase shapes the React components expect.

/** API base URL. The PWA is served same-origin by Rails, so default to "".
 *  An Inertia bootstrap prop may override via window.__API_URL__. */
export function apiBase(): string {
  const override = (globalThis as { __API_URL__?: string }).__API_URL__;
  return (override ?? "").replace(/\/+$/, "");
}

interface RequestOpts {
  auth?: string | null;
  body?: unknown;
}

// A 401 on an authenticated request means the server no longer recognizes this
// device — almost always because the member was removed from the family (e.g.
// booted on a downgrade to Free). Broadcast it so the app can wipe local state
// and return to onboarding instead of leaving stale family data readable.
function notifyUnauthorized(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("fam:unauthorized"));
}

// Carries the HTTP status + parsed body alongside the message, so callers can react
// to specific failures (e.g. the relay's 409 { resync_required }) without losing the
// existing `error.message` contract — ApiError extends Error.
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function request<T>(method: string, path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.auth) headers["Authorization"] = `Bearer ${opts.auth}`;

  let resp: Response;
  try {
    resp = await fetch(apiBase() + path, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    throw new Error(`Network error: ${e}`);
  }

  if (!resp.ok) {
    if (resp.status === 401 && opts.auth) notifyUnauthorized();
    let message = "Unknown server error";
    let body: unknown;
    try {
      body = await resp.json();
      const err = (body as { error?: string })?.error;
      if (err) message = err;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, message, body);
  }

  const text = await resp.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const get = <T>(path: string, auth?: string | null) => request<T>("GET", path, { auth });
const post = <T>(path: string, auth: string | null, body?: unknown) =>
  request<T>("POST", path, { auth, body });
const patch = <T>(path: string, auth: string | null, body?: unknown) =>
  request<T>("PATCH", path, { auth, body });

// --- Response shapes (snake_case, as the server returns) ---

export interface CreateFamilyResponse {
  family_id: number;
  family_name: string;
  member_id: number;
  member_name: string;
  device_id: number;
  auth_token: string;
}
export interface CreateInviteResponse {
  code: string;
  expires_at: string;
  family_name: string;
  device_link?: boolean;
}
export interface MemberPreview {
  id: number;
}
export interface PreviewInviteResponse {
  family_name: string;
  members: MemberPreview[];
  expires_at: string;
  device_link?: boolean;
  link_member_name?: string | null;
}
export interface JoinInviteResponse {
  status: string;
  join_request_id: number;
  family_name: string;
  // Secret returned once at join time; required to poll the join request's status
  // (the server 404s without it). Persisted locally and replayed on each poll.
  claim_token: string;
}
export interface SnapshotEntry {
  doc_id: string;
  doc_type: string;
  // null for legacy rows predating Phase 4 (titles were plaintext, dropped by migration).
  encrypted_title: string | null;
  encrypted_snapshot: string;
  sequence_at: number;
}
export interface JoinStatusResponse {
  status: string;
  family_id?: number;
  family_name?: string;
  member_id?: number;
  member_name?: string;
  device_id?: number;
  auth_token?: string;
  wrapped_key?: string;
  approver_public_key?: string;
  snapshots?: SnapshotEntry[];
}
export interface JoinRequestEntry {
  id: number;
  member_name: string;
  public_key: string;
  status: string;
  device_link: boolean;
  created_at: string;
}
export interface ServerMemberInfo {
  id: number;
  encrypted_display_name: string | null;
  role: string;
  online: boolean;
  last_active_at: string | null;
  is_self: boolean;
  avatar_url: string | null;
  avatar_encrypted: boolean;
}
export interface SubscriptionResponse {
  enabled: boolean;
  tier: "free" | "duo" | "family";
  status: string;
  source: "stripe" | "promo" | null;
  auto_renew: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  original_purchased_at: string | null;
  canceled_at: string | null;
  member_count: number;
  member_limit: number;
  prices: { duo: number; family: number };
  message?: string;
}
export interface PushSyncResponse {
  sequence: number;
}
export interface SyncUpdateEntry {
  sequence: number;
  doc_id: string;
  doc_type: string;
  // null for legacy rows predating Phase 4 (titles were plaintext, dropped by migration).
  encrypted_title: string | null;
  encrypted_update: string;
}
export interface ServerChatMessage {
  id: number;
  sender_member_id: number;
  encrypted_content: string;
  created_at: string;
}
export interface ServerRecentMessage {
  id: number;
  channel: string;
  sender_member_id: number;
  created_at: string;
}

// --- Endpoints (one method per api.rs public method) ---

export const createFamily = (familyName: string, memberName: string, publicKey: string) =>
  post<CreateFamilyResponse>("/families", null, {
    family_name: familyName,
    member_name: memberName,
    public_key: publicKey,
  });

export const createInvite = (auth: string, familyId: number, deviceLink = false) =>
  post<CreateInviteResponse>(`/families/${familyId}/invites`, auth, { device_link: deviceLink });

export const previewInvite = (code: string) =>
  get<PreviewInviteResponse>(`/invites/${code}`);

export const joinInvite = (code: string, memberName: string, publicKey: string) =>
  post<JoinInviteResponse>(`/invites/${code}/join`, null, {
    member_name: memberName,
    public_key: publicKey,
  });

export const getFamilySettings = (auth: string, familyId: number) =>
  get<{ id: number; name: string; chat_retention_days: number }>(`/families/${familyId}`, auth);

export const updateFamilySettings = (auth: string, familyId: number, chatRetentionDays: number) =>
  request<{ id: number; name: string; chat_retention_days: number }>("PATCH", `/families/${familyId}`, {
    auth,
    body: { chat_retention_days: chatRetentionDays },
  });

export const listMembers = (auth: string, familyId: number) =>
  get<{ members: ServerMemberInfo[] }>(`/families/${familyId}/members`, auth).then((r) => r.members);

export const updateMember = (auth: string, familyId: number, memberId: number, encryptedDisplayName: string) =>
  request<void>("PATCH", `/families/${familyId}/members/${memberId}`, {
    auth,
    body: { encrypted_display_name: encryptedDisplayName },
  });

export const deleteMember = (auth: string, familyId: number, memberId: number) =>
  request<void>("DELETE", `/families/${familyId}/members/${memberId}`, { auth });

export const uploadAvatar = (auth: string, familyId: number, memberId: number, imageB64: string) =>
  post<void>(`/families/${familyId}/members/${memberId}/avatar`, auth, {
    image: imageB64,
  });

export const pushSync = (
  auth: string,
  docId: string,
  docType: string,
  encryptedTitle: string,
  encryptedUpdate: string,
) =>
  post<PushSyncResponse>("/sync/push", auth, {
    doc_id: docId,
    doc_type: docType,
    encrypted_title: encryptedTitle,
    encrypted_update: encryptedUpdate,
  });

export const pullSync = (auth: string, afterSequence: number) =>
  get<{ updates: SyncUpdateEntry[] }>(`/sync/pull?after=${afterSequence}`, auth).then(
    (r) => r.updates,
  );

export const pushSnapshots = (auth: string, inviteCode: string, snapshots: SnapshotEntry[]) =>
  post<void>(`/invites/${inviteCode}/snapshots`, auth, { snapshots });

// Current frontend build path (its content hash changes every deploy). Unauthenticated.
export const getBuildId = () => get<{ build: string | null }>("/version").then((r) => r.build);

// --- Peer-handoff resync (Phase 1b) ---

export interface ResyncRequestSummary {
  id: number;
  requester_device_id: number;
}

export const createResyncRequest = (auth: string) =>
  post<{ id: number; status: string }>(`/resync_requests`, auth, {});

export const listResyncRequests = (auth: string) =>
  get<{ resync_requests: ResyncRequestSummary[] }>(`/resync_requests`, auth).then(
    (r) => r.resync_requests,
  );

export const getResyncRequest = (auth: string, id: number) =>
  get<{ status: string; snapshots: SnapshotEntry[] }>(`/resync_requests/${id}`, auth);

export const fulfillResyncRequest = (auth: string, id: number, snapshots: SnapshotEntry[]) =>
  post<void>(`/resync_requests/${id}/fulfill`, auth, { snapshots });

export const checkJoinStatus = (joinRequestId: number, claimToken: string) =>
  get<JoinStatusResponse>(
    `/join_requests/${joinRequestId}/status?claim_token=${encodeURIComponent(claimToken)}`,
  );

export const listJoinRequests = (auth: string, familyId: number) =>
  get<{ join_requests: JoinRequestEntry[] }>(`/families/${familyId}/join_requests`, auth).then(
    (r) => r.join_requests,
  );

export const approveJoinRequest = (
  auth: string,
  familyId: number,
  requestId: number,
  wrappedKey: string,
  approverPublicKey: string,
) =>
  post<void>(`/families/${familyId}/join_requests/${requestId}/approve`, auth, {
    wrapped_key: wrappedKey,
    approver_public_key: approverPublicKey,
  });

export const rejectJoinRequest = (auth: string, familyId: number, requestId: number) =>
  post<void>(`/families/${familyId}/join_requests/${requestId}/reject`, auth, {});

export const uploadPhoto = (auth: string, fullCiphertextB64: string, thumbCiphertextB64: string) =>
  post<{ processing_token: string }>("/photos", auth, {
    full_ciphertext: fullCiphertextB64,
    thumb_ciphertext: thumbCiphertextB64,
  });

export const getPhotoStatus = (
  auth: string,
  token: string,
) =>
  get<{ status: string; photo_url?: string; thumb_url?: string }>(
    `/photos/${token}`,
    auth,
  );

// Fetch an opaque (ciphertext) image blob by its server-minted URL and return the
// raw bytes. The URL is a signed Active Storage proxy URL handed back by
// PhotosController#show / MembersController#index; the signed id is itself the
// capability, so the Bearer token is not strictly required, but we send it when
// present to keep every server request uniformly authenticated (and to surface the
// same "fam:unauthorized" signal on a 401). The caller decrypts these bytes with
// the group key — the server only ever stores ciphertext for images.
export async function fetchBlob(url: string, auth?: string | null): Promise<Uint8Array> {
  const headers: Record<string, string> = {};
  if (auth) headers["Authorization"] = `Bearer ${auth}`;

  // The token is already a full (absolute) proxy URL minted by the server, so it
  // is fetched as-is; a relative URL would still resolve against the same origin.
  let resp: Response;
  try {
    resp = await fetch(url, { headers });
  } catch (e) {
    throw new Error(`Network error: ${e}`);
  }

  if (!resp.ok) {
    if (resp.status === 401 && auth) notifyUnauthorized();
    throw new ApiError(resp.status, `Blob fetch failed (${resp.status})`, null);
  }

  return new Uint8Array(await resp.arrayBuffer());
}

export const getRecentChat = (auth: string, after: number) =>
  get<{ messages: ServerRecentMessage[] }>(`/chat/recent?after=${after}`, auth).then(
    (r) => r.messages,
  );

export const sendChatMessage = (auth: string, encryptedContent: string, channel: string) =>
  post<{ id: number; created_at: string }>("/chat/messages", auth, {
    encrypted_content: encryptedContent,
    channel,
  });

export const getChatMessages = (auth: string, after: number, limit: number, channel: string) =>
  get<{ messages: ServerChatMessage[] }>(
    `/chat/messages?after=${after}&limit=${limit}&channel=${channel}`,
    auth,
  ).then((r) => r.messages);

// All channels, content included — the eager catch-up pull (Phase 3).
export interface ServerChatSinceMessage {
  id: number;
  channel: string;
  sender_member_id: number;
  encrypted_content: string;
  created_at: string;
}

export const getChatSince = (auth: string, after: number) =>
  get<{ messages: ServerChatSinceMessage[] }>(`/chat/since?after=${after}`, auth).then(
    (r) => r.messages,
  );

export const reportCrash = (message: string, stack: string, version: string, platform: string) =>
  post<void>("/crashes", null, { message, stack, version, platform });

// --- Web Push subscription (device resolved server-side from the token) ---

export const savePushSubscription = (auth: string, subscription: PushSubscriptionJSON) =>
  post<void>("/push_subscription", auth, { subscription });

export const deletePushSubscription = (auth: string) =>
  request<void>("DELETE", "/push_subscription", { auth });

// --- Subscription / billing (the family is resolved server-side from the token) ---

export const getSubscription = (auth: string) =>
  get<SubscriptionResponse>("/subscription", auth);

export const createCheckout = (auth: string, tier: "duo" | "family") =>
  post<{ url: string }>("/subscription/checkout", auth, { tier });

export const changeTier = (auth: string, tier: "duo" | "family") =>
  patch<SubscriptionResponse>("/subscription", auth, { tier });

export const setAutoRenew = (auth: string, autoRenew: boolean) =>
  patch<SubscriptionResponse>("/subscription", auth, { auto_renew: autoRenew });

export const cancelSubscription = (auth: string) =>
  post<SubscriptionResponse>("/subscription/cancel", auth, {});

export const redeemPromo = (auth: string, code: string) =>
  post<SubscriptionResponse>("/subscription/promo", auth, { code });

// Dev/test-only: add bare roster members to the authenticated family. Names arrive
// already encrypted with the group key (the server never sees plaintext). Backed by
// DevController#add_members, which only exists outside production. Used by the
// Lebowski seed (lib/seed.ts), never by the app proper.
export const devSeedMembers = (auth: string, encryptedNames: string[]) =>
  post<{ members: { id: number }[] }>("/dev/members", auth, { encrypted_names: encryptedNames }).then(
    (r) => r.members,
  );
